import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../store';
import { requestNavigateMainTab } from '../../store/slices/modalsSlice';

interface TrialExpiredWallProps {
  /** Suppress the overlay on the billing/settings surface so the locked user
   *  can actually reach checkout (the wall is z-1000 fixed and would otherwise
   *  cover the billing page its own CTA navigates to). */
  suppressed?: boolean;
}

/**
 * Full-screen blocking paywall shown once the 14-day auto-trial has lapsed
 * and the user has not subscribed. Backend (HTTP 402) is the real gate; this
 * is the UX surface. Self-hides when not locked (or when on the billing
 * surface), so it is mounted unconditionally (next to DisclaimerGateModal in
 * App.tsx). Reuses surface tokens per the theming contract.
 */
const TrialExpiredWall: React.FC<TrialExpiredWallProps> = ({ suppressed = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isLocked = useAppSelector((s) => s.billing.isLocked);
  if (!isLocked || suppressed) return null;

  const goToBilling = () => dispatch(requestNavigateMainTab('billing'));

  return (
    <div
      data-testid="trial-expired-wall"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-[color:var(--overlay)] p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-2xl bg-surface-container-high p-8 text-center shadow-xl">
        <h2 className="text-xl font-semibold text-text-primary">
          {t('trial.expiredTitle', 'Your free trial has ended')}
        </h2>
        <p className="mt-3 text-text-secondary">
          {t(
            'trial.expiredBody',
            'Subscribe to Pro to keep screening, analyzing, and tracking your positions.',
          )}
        </p>
        <button
          type="button"
          onClick={goToBilling}
          className="mt-6 w-full rounded-lg bg-accent-primary px-4 py-2.5 font-semibold text-on-primary transition-opacity hover:opacity-90"
        >
          {t('trial.expiredCta', 'Subscribe to Pro')}
        </button>
      </div>
    </div>
  );
};

export default TrialExpiredWall;
